// A tiny, deterministic reading of the common hours_text forms ("Tue-Sat 10am-5pm; closed Mon"). Pure, no imports, so
// the app (through schedule.ts lateToday / daytimeStart) and the server (times.ts usableHours) read hours text the same
// way (B-089 review). Moved verbatim out of times.ts.
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
export type Weekly = Record<string, number[][] | boolean> & { unclear?: boolean }
const DAY_IX: Record<string, number> = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 }
const DAY_RE = '(?:sun(?:day)?|mon(?:day)?|tue(?:s(?:day)?)?|wed(?:nesday)?|thu(?:r(?:s(?:day)?)?)?|fri(?:day)?|sat(?:urday)?)s?(?![a-z])\\.?'
const TIME_RE = '(?:\\d{1,2}(?::\\d{2})?\\s*(?:am|pm|a\\.m\\.|p\\.m\\.)?|noon|midnight|sunrise|sunset|dawn|dusk)'
const MONTHS = /\b(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|june?|july?|aug(?:ust)?|sept?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\b/i
function clock(raw: string): { h: number; ap: 'am' | 'pm' | null } | null {
  const w = raw.trim().toLowerCase().replace(/\./g, '')
  const word: Record<string, [number, 'am' | 'pm']> = { noon: [12, 'pm'], midnight: [24, 'pm'], sunrise: [7, 'am'], dawn: [7, 'am'], sunset: [18, 'pm'], dusk: [18, 'pm'] }
  if (word[w]) return { h: word[w][0], ap: word[w][1] }
  const m = w.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/)
  if (!m) return null
  const h = Number(m[1]), min = m[2] ? Number(m[2]) / 60 : 0
  if (h > 24 || min >= 1) return null
  return { h: h + min, ap: (m[3] as 'am' | 'pm' | undefined) ?? null }
}
const to24 = (h: number, ap: 'am' | 'pm') => (ap === 'am' ? (h >= 12 ? h - 12 : h) : h < 12 ? h + 12 : h)
function range(a: string, b: string): [number, number] | null {
  const x = clock(a), y = clock(b)
  if (!x || !y) return null
  let s: number, e: number
  if (x.ap && y.ap) { s = to24(x.h, x.ap); e = to24(y.h, y.ap) }
  else if (y.ap) { e = to24(y.h, y.ap); s = to24(x.h, y.ap); if (s > e) s = to24(x.h, 'am') } // "10-5pm"
  else if (x.ap) { s = to24(x.h, x.ap); e = y.h; if (e <= s) e = to24(y.h, 'pm') } // "4pm-10"
  else { s = x.h < 7 ? x.h + 12 : x.h; e = y.h <= s ? y.h + 12 : y.h } // "daily 9-4"
  if (e <= s) e += 24 // "4pm-2am"
  return e - s >= 0.5 && e - s <= 24 ? [s, e] : null
}
function daysIn(seg: string): number[] | null {
  if (/\b(?:daily|every ?day|7 days|seven days)\b/.test(seg)) return [0, 1, 2, 3, 4, 5, 6]
  const out = new Set<number>()
  for (const m of seg.matchAll(new RegExp(`\\b(${DAY_RE})\\s*-\\s*(${DAY_RE})`, 'g'))) {
    const a = DAY_IX[m[1].slice(0, 3)], b = DAY_IX[m[2].slice(0, 3)]
    for (let d = a; ; d = (d + 1) % 7) { out.add(d); if (d === b) break }
  }
  const rest = seg.replace(new RegExp(`\\b${DAY_RE}\\s*-\\s*${DAY_RE}`, 'g'), ' ')
  for (const m of rest.matchAll(new RegExp(`\\b(${DAY_RE})(?=[\\s,&/]|$)`, 'g'))) { const d = DAY_IX[m[1].slice(0, 3)]; if (d != null) out.add(d) }
  if (/\bweekdays?\b/.test(seg)) [1, 2, 3, 4, 5].forEach((d) => out.add(d))
  if (/\bweekends?\b/.test(seg)) [6, 0].forEach((d) => out.add(d))
  return out.size ? [...out] : null
}
/** "10am-5pm", "Sat-Sun 10am-5pm", "daily 9-4", "Tue-Sat 10am-5pm; closed Mon" -> weekly hours, or null when the text
 *  doesn't say (no range, month-by-month seasons, "opens 8am"). */
export function hoursFromText(text: string | null | undefined): Weekly | null {
  const t = String(text ?? '').toLowerCase().replace(/[–—]/g, '-').replace(/\s+to\s+/g, '-').replace(/\s+/g, ' ')
  if (!t.trim() || MONTHS.test(t)) return null
  const out: Weekly = {}
  let any = false
  const segs = t.split(/;|\.\s|,(?=\s*(?:daily|weekends?|weekdays?|(?:sun|mon|tue|wed|thu|fri|sat)[a-z]*\b))/)
  const closesOnly: [string, string][] = []
  for (const seg of segs) {
    const m = seg.match(new RegExp(`(${TIME_RE})\\s*-\\s*(${TIME_RE})`))
    if (!m) { const c = seg.match(new RegExp(`\\bcloses?(?: at)? (${TIME_RE})`)); if (c) closesOnly.push([seg.slice(0, c.index), c[1]]); continue }
    const r = range(m[1], m[2])
    if (!r) continue
    const days = daysIn(seg.slice(0, m.index)) ?? daysIn(seg) ?? [0, 1, 2, 3, 4, 5, 6]
    for (const d of days) { const k = DAY_KEYS[d]; out[k] = [...((out[k] as number[][] | undefined) ?? []), r]; any = true }
  }
  if (!any) return null
  // "Sat-Sun closes 5pm": the days it names, from the earliest opening the text gives, to that closing
  const open = Math.min(...Object.values(out).flatMap((v) => (Array.isArray(v) ? v.map((x) => x[0]) : [])))
  for (const [pre, at] of closesOnly) {
    const c = clock(at), days = daysIn(pre)
    if (!c || !days) continue
    const e = to24(c.h, c.ap ?? 'pm')
    if (e - open >= 1) for (const d of days) if (!out[DAY_KEYS[d]]) out[DAY_KEYS[d]] = [[open, e]]
  }
  for (const m of t.matchAll(new RegExp(`\\bclosed (?:on )?((?:${DAY_RE}(?:\\s*(?:-|,|&|and|/)\\s*)?)+)`, 'g'))) for (const d of daysIn(m[1]) ?? []) out[DAY_KEYS[d]] = []
  out.unclear = false
  return out
}
